// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * Getting a project folder into the tab, by whichever of five routes this
 * browser supports.
 *
 * The ladder, best first:
 *   1. `showDirectoryPicker()` in DEFAULT READ MODE            Chromium
 *   2. a dropped folder → `getAsFileSystemHandle()`            Chromium
 *   3. `<input webkitdirectory>`                               Firefox, Safari, iOS 18.4+
 *   4. a dropped folder → `webkitGetAsEntry` + `readEntries`   Firefox, Safari
 *   5. a dropped or picked `.zip`                              everywhere, including iOS 17
 *
 * RUNG 1 MUST NOT ASK FOR `{mode:'readwrite'}`. `DirectoryPickerOptions.mode`
 * defaults to `"read"`, and Chromium only consults the write guard when the mode
 * is readwrite — so under an IT policy that blocks File System writes, a
 * read-mode picker still works and the user gets the whole read-only product: a
 * real diff against real bytes, elision refusal checked against actual on-disk
 * content, and a `git apply`-clean patch. Asking for readwrite up front turns
 * that into a bare `NotAllowedError` at step one. Write permission is requested
 * later, from its own click, only if the user asks to write.
 */

import { unzipSync } from 'fflate';
import { detectEol } from '../engine/eol';
import type { SourceFile } from '../types';

/** Never walked, whatever the ignore rules say. `.git/objects` alone is enormous. */
const NEVER_WALK = new Set(['.git', '.hg', '.svn', 'node_modules', '.next', '.nuxt', '.turbo', '.venv', '__pycache__']);

/** A file bigger than this is listed but never read — nobody pastes a 5 MB file. */
export const MAX_FILE_BYTES = 1_000_000;
export const MAX_FILES = 4000;

export interface RawFile {
  readonly path: string;
  readonly size: number;
  readonly file: File;
}

export interface ReadResult {
  readonly files: RawFile[];
  readonly folderName: string;
  /** Present only on the Chromium rungs, and the only thing that enables writing. */
  readonly handle: FileSystemDirectoryHandle | null;
  readonly truncated: boolean;
}

export type Progress = (seen: number, path: string) => void;

/* ── rungs 1 and 2: real handles ──────────────────────────────────────────── */

export async function pickDirectory(): Promise<FileSystemDirectoryHandle> {
  // No `mode` — the default is 'read'. See the header.
  return (window as unknown as { showDirectoryPicker(o?: object): Promise<FileSystemDirectoryHandle> })
    .showDirectoryPicker({ id: 'unfence-project' });
}

export async function readHandle(
  root: FileSystemDirectoryHandle,
  onProgress?: Progress,
  signal?: AbortSignal,
): Promise<ReadResult> {
  const files: RawFile[] = [];
  let truncated = false;

  async function walk(dir: FileSystemDirectoryHandle, prefix: string): Promise<void> {
    if (files.length >= MAX_FILES) {
      truncated = true;
      return;
    }
    // `values()` costs one IPC round trip per entry in Chrome and there is no
    // way to batch it, so this is the slow part of opening a large folder and
    // the progress callback is what stops it looking hung.
    for await (const entry of (dir as unknown as { values(): AsyncIterable<FileSystemHandle> }).values()) {
      if (signal?.aborted) return;
      if (files.length >= MAX_FILES) {
        truncated = true;
        return;
      }
      const path = prefix ? `${prefix}/${entry.name}` : entry.name;
      if (entry.kind === 'directory') {
        if (NEVER_WALK.has(entry.name)) continue;
        await walk(entry as FileSystemDirectoryHandle, path);
      } else {
        const file = await (entry as FileSystemFileHandle).getFile();
        files.push({ path, size: file.size, file });
        onProgress?.(files.length, path);
      }
    }
  }

  await walk(root, '');
  return { files, folderName: root.name, handle: root, truncated };
}

/**
 * A dropped folder, on Chromium, yielding a writable-capable handle.
 *
 * Worth preferring over the picker where the user reaches for it: Chrome refuses
 * `$HOME`, Desktop, Documents and Downloads outright in the PICKER (a repository
 * inside them is fine), and a drop side-steps that. The drop event also carries
 * transient activation — Chromium calls `NotifyUserActivation` in
 * `DragController::PerformDrop` before the event is dispatched — so a later
 * `requestPermission()` from a drop handler is on solid ground.
 */
export async function handleFromDrop(items: DataTransferItemList): Promise<FileSystemDirectoryHandle | null> {
  for (const item of Array.from(items)) {
    const get = (item as unknown as { getAsFileSystemHandle?(): Promise<FileSystemHandle | null> })
      .getAsFileSystemHandle;
    if (typeof get !== 'function') return null;
    const handle = await get.call(item);
    if (handle && handle.kind === 'directory') return handle as FileSystemDirectoryHandle;
  }
  return null;
}

/* ── rung 4: the Entries API ──────────────────────────────────────────────── */

interface FsEntry {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
  file?(cb: (f: File) => void, err: (e: unknown) => void): void;
  createReader?(): { readEntries(cb: (e: FsEntry[]) => void, err: (e: unknown) => void): void };
}

export async function readDroppedEntries(items: DataTransferItemList, onProgress?: Progress): Promise<ReadResult> {
  const roots: FsEntry[] = [];
  for (const item of Array.from(items)) {
    const entry = (item as unknown as { webkitGetAsEntry?(): FsEntry | null }).webkitGetAsEntry?.();
    if (entry) roots.push(entry);
  }

  const files: RawFile[] = [];
  let truncated = false;

  async function readAll(reader: { readEntries(cb: (e: FsEntry[]) => void, err: (e: unknown) => void): void }) {
    const out: FsEntry[] = [];
    // `readEntries` returns AT MOST 100 entries per call and says nothing when
    // it stops — an EMPTY ARRAY is the end-of-directory signal. Concurrent calls
    // on one reader are rejected, so each must be awaited before the next.
    for (;;) {
      const batch = await new Promise<FsEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
      if (batch.length === 0) return out;
      out.push(...batch);
      if (out.length > MAX_FILES) return out;
    }
  }

  async function walk(entry: FsEntry, prefix: string): Promise<void> {
    if (files.length >= MAX_FILES) {
      truncated = true;
      return;
    }
    const path = prefix ? `${prefix}/${entry.name}` : entry.name;
    if (entry.isDirectory) {
      if (NEVER_WALK.has(entry.name)) return;
      const reader = entry.createReader?.();
      if (!reader) return;
      for (const child of await readAll(reader)) await walk(child, path);
    } else if (entry.isFile && entry.file) {
      const file = await new Promise<File>((resolve, reject) => entry.file!(resolve, reject));
      files.push({ path, size: file.size, file });
      onProgress?.(files.length, path);
    }
  }

  const folderName = roots.find((r) => r.isDirectory)?.name ?? 'project';
  for (const root of roots) await walk(root, root.isDirectory && roots.length === 1 ? '' : '');
  return { files, folderName, handle: null, truncated };
}

/* ── rungs 3 and 5: plain FileList ────────────────────────────────────────── */

export type FileListShape = 'tree' | 'flat-no-paths' | 'empty';

/**
 * Three outcomes, not two.
 *
 * `webkitRelativePath` empty on every file is Firefox Android 141, where folder
 * selection works but the relative paths do not (bug 1973726) — that is a route
 * to drag-and-drop, not a failure. An EMPTY FileList after the change event is
 * iOS below 18.4, where the property exists, can be set, and does nothing.
 */
export function shapeOf(list: FileList | File[]): FileListShape {
  const files = Array.from(list);
  if (files.length === 0) return 'empty';
  return files.some((f) => f.webkitRelativePath) ? 'tree' : 'flat-no-paths';
}

export function readFileList(list: FileList | File[]): ReadResult {
  const all = Array.from(list);
  const files: RawFile[] = [];
  let folderName = 'project';
  let truncated = false;

  for (const file of all) {
    if (files.length >= MAX_FILES) {
      truncated = true;
      break;
    }
    const relative = file.webkitRelativePath || file.name;
    const parts = relative.split('/');
    if (parts.length > 1) {
      folderName = parts[0];
      // The picker prefixes every path with the chosen folder's own name; the
      // tool's paths are relative to that folder, so it comes off.
      parts.shift();
    }
    const path = parts.join('/');
    if (parts.some((p) => NEVER_WALK.has(p))) continue;
    files.push({ path, size: file.size, file });
  }
  return { files, folderName, handle: null, truncated };
}

/* ── the universal path: a zip ────────────────────────────────────────────── */

export async function readZip(blob: Blob, name = 'project'): Promise<ReadResult> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const files: RawFile[] = [];
  let truncated = false;
  // The filter runs before anything is inflated, so a `.git` directory or a
  // 200 MB asset costs nothing but a callback.
  const entries = unzipSync(bytes, {
    filter: (entry) => {
      if (entry.name.endsWith('/')) return false;
      if (entry.originalSize > MAX_FILE_BYTES) return false;
      const parts = entry.name.split('/');
      return !parts.some((p) => NEVER_WALK.has(p));
    },
  });

  // A zip made by "compress this folder" wraps everything in one top-level
  // directory. Stripping it keeps paths matching what the user sees.
  const names = Object.keys(entries);
  const first = names[0]?.split('/')[0] ?? '';
  const wrapped = Boolean(first) && names.every((n) => n.startsWith(`${first}/`));

  for (const [entryName, data] of Object.entries(entries)) {
    if (files.length >= MAX_FILES) {
      truncated = true;
      break;
    }
    const path = wrapped ? entryName.slice(first.length + 1) : entryName;
    if (!path) continue;
    files.push({ path, size: data.byteLength, file: new File([data as BlobPart], path) });
  }
  return { files, folderName: wrapped ? first : name.replace(/\.zip$/i, ''), handle: null, truncated };
}

/* ── decoding ─────────────────────────────────────────────────────────────── */

/**
 * Read a file as text, or decide it is not text.
 *
 * A NUL byte in the first 8 KB is the binary sniff — the same rule git uses, and
 * far more reliable than an extension list, which cannot know about the
 * project's own formats. A file that is not text is still LISTED, so the user
 * can see it exists; it is simply never put in a bundle a model would choke on.
 */
export async function decode(raw: RawFile): Promise<SourceFile> {
  const base: Omit<SourceFile, 'text' | 'eol'> = {
    path: raw.path,
    size: raw.size,
    ignored: false,
    refused: null,
  };
  if (raw.size > MAX_FILE_BYTES) return { ...base, text: null, eol: '\n' };

  const buffer = new Uint8Array(await raw.file.arrayBuffer());
  const head = buffer.subarray(0, 8192);
  for (let i = 0; i < head.length; i++) {
    if (head[i] === 0) return { ...base, text: null, eol: '\n' };
  }
  let text: string;
  try {
    // Fatal, so invalid UTF-8 is reported as binary rather than silently
    // decoded with replacement characters that would then be WRITTEN BACK,
    // corrupting a file the tool was only meant to read.
    text = new TextDecoder('utf-8', { fatal: true }).decode(buffer);
  } catch {
    return { ...base, text: null, eol: '\n' };
  }
  return { ...base, text, eol: detectEol(text) };
}
